import { styled } from '@mui/material/styles';
import {
  Box,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { ReactEventHandler } from 'react';

const { __environment__ } = window as any;

const molinaAppURL = __environment__.REACT_APP_MOLINA_APP_URL;

const scheduleTransportationURL = `${molinaAppURL}/auth`;
const pcsURL = 'https://molina.americanlogistics.com/pcs';
const americanlogisticsURL = 'https://molina.americanlogistics.com';
const americanlogisticsTelephone = '(844) 292-2688';
const molinaTelephone = '(888) 665-4621';

const languages = [
  'ES',
  'AR',
  'FA',
  'HM',
  'HY',
  'KM',
  'KO',
  'RU',
  'TL',
  'VI',
  'ZH',
  'ZS',
];

const PDFFileNames: { [key: string]: string } = {
  ES: 'gen_al_moina_overview_r2-ES',
  AR: 'gen_al_moina_overview_r2-AR',
  FA: 'gen_al_moina_overview_r2-FA',
  HM: 'gen_al_moina_overview_r2-HM',
  HY: 'gen_al_moina_overview_r2-HY',
  KM: 'gen_al_moina_overview_r2-KM',
  KO: 'gen_al_moina_overview_r2-KO',
  RU: 'gen_al_moina_overview_r2-RU',
  TL: 'gen_al_moina_overview_r2-TL',
  VI: 'gen_al_moina_overview_r2-VI',
  ZH: 'gen_al_moina_overview_r2-ZH',
  ZS: 'gen_al_moina_overview_r2-ZS',
};

const PREFIX = 'Home';

const classes = {
  container: `${PREFIX}-container`,
  heroContent: `${PREFIX}-content`,
  heroText: `${PREFIX}-heroText`,
  heroTitle1: `${PREFIX}-heroTitle1`,
  heroTitle2: `${PREFIX}-heroTitle2`,
  topHeader: `${PREFIX}-topHeader`,
  language: `${PREFIX}-language`,
  title: `${PREFIX}-title`,
  heroDescription: `${PREFIX}-heroDescription`,
  heroCTA: `${PREFIX}-heroCTA`,
  logo1: `${PREFIX}-logo1`,
  logo2: `${PREFIX}-logo2`,
  mainContent: `${PREFIX}-mainContent`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  fontSource: `${PREFIX}-fontSource`,
  fontBitter: `${PREFIX}-fontBitter`,
  listItem: `${PREFIX}-listItem`,
  whiteBorder: `${PREFIX}-whiteBorder`,
  downloadBox: `${PREFIX}-downloadBox`,
  footer: `${PREFIX}-footer`,
  footerMenu: `${PREFIX}-footerMenu`,
  link: `${PREFIX}-link`,
  scheduleBtn: `${PREFIX}-scheduleBtn`,
  pcsBtn: `${PREFIX}-pcsBtn`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: theme.palette.info,
    width: '100%',
  },

  [`& .${classes.heroContent}`]: {
    display: 'flex',
    flex: '0 1 100%',
    flexDirection: 'column',
    backgroundSize: 'cover',
    backgroundImage: 'url(/home_hero.png)',
    paddingTop: 160,
    paddingLeft: 150,
    paddingRight: 150,
    paddingBottom: 100,
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      paddingTop: 120,
      paddingLeft: 20,
      paddingRight: 20,
      paddingBottom: 40,
    },
  },

  [`& .${classes.heroText}`]: {},

  [`& .${classes.heroTitle1}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    width: 'fit-content',
  },

  [`& .${classes.heroTitle2}`]: {
    fontFamily: 'Bitter',
    whiteSpace: 'pre-line',
    fontWeight: 700,
    fontSize: 72,
    lineHeight: '80px',
    width: 'fit-content',
    [theme.breakpoints.down('md')]: {
      fontSize: 36,
      lineHeight: '48px',
    },
  },

  [`& .${classes.topHeader}`]: {
    display: 'flex',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 147,
    paddingLeft: 60,
    paddingRight: 60,
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      paddingLeft: 20,
      paddingRight: 20,
    },
  },

  [`& .${classes.language}`]: {
    height: 36,
    width: 225,
    border: '1px solid #197CB34D',
    '& .MuiSelect-select': {
      color: theme.palette.primary.light,
    },
  },

  [`& .${classes.heroDescription}`]: {
    fontFamily: 'Source Sans Pro',
    fontWeight: 400,
    fontSize: 24,
    color: 'rgba(1, 16, 18, 0.9)',
    lineHeight: '32px',
    width: '50%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      fontSize: '16px',
      lineHeight: '24px',
    },
  },

  [`& .${classes.heroCTA}`]: {
    display: 'flex',
    marginTop: 50,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },

  [`& .${classes.scheduleBtn}`]: {
    [theme.breakpoints.down('md')]: {
      width: 250,
    },
  },

  [`& .${classes.pcsBtn}`]: {
    marginLeft: 20,

    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      width: 150,
      marginTop: 20,
    },
  },

  [`& .${classes.logo1}`]: {
    // width: 400,
    [theme.breakpoints.down('md')]: {
      width: 160,
    },
  },

  [`& .${classes.logo2}`]: {
    width: 220,

    '& img': {
      width: '100%',
    },

    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  [`& .${classes.mainContent}`]: {
    display: 'flex',
    flex: '0 1 100%',
    padding: '100px 150px',
    backgroundColor: theme.palette.primary.dark,

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      padding: '50px 20px',
    },
  },

  [`& .${classes.left}`]: {
    flex: 3,
  },

  [`& .${classes.right}`]: {
    flex: 2,
    paddingLeft: 20,
    [theme.breakpoints.down('md')]: {
      paddingLeft: 0,
    },
  },

  [`& .${classes.fontSource}`]: {
    fontFamily: 'Source Sans Pro',
    color: theme.palette.secondary.light,
  },

  [`& .${classes.fontBitter}`]: {
    fontFamily: 'Bitter',
    color: theme.palette.secondary.light,
  },

  [`& .${classes.listItem}`]: {
    display: 'list-item',
    color: theme.palette.secondary.light,
    marginLeft: 30,
    fontSize: 21,
    padding: 0,
    lineHeight: '28px',

    [theme.breakpoints.down('md')]: {
      fontSize: 16,
    },
  },

  [`& .${classes.whiteBorder}`]: {
    border: `1px solid ${theme.palette.secondary.light}`,
  },

  [`& .${classes.downloadBox}`]: {
    border: `1px solid ${theme.palette.secondary.light}`,
    borderTop: 'none',
    paddingLeft: '30px',
    color: theme.palette.secondary.light,
  },

  [`& .${classes.footer}`]: {
    height: 160,
    paddingLeft: 60,
    paddingRight: 60,
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    backgroundColor: 'rgba(1, 16, 18, 0.9)',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      height: 'fit-content',
      paddingTop: 50,
      paddingBottom: 50,
      alignItems: 'start',
    },
  },

  [`& .${classes.footerMenu}`]: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'start',
      marginTop: 20,
    },
  },

  [`& .${classes.link}`]: {
    fontFamily: 'Source Sans Pro',
    color: theme.palette.secondary.light,
    fontSize: 16,
    textDecoration: 'none',
    marginLeft: 5,
    marginRight: 20,
    [theme.breakpoints.down('md')]: {
      marginTop: 20,
    },
  },
}));

export default function Home() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));

  const downloadPDF = (fileName: string) => {
    fetch(fileName).then((response) => {
      response.blob().then((blob) => {
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);

        // Setting various property values
        let alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = fileName;
        alink.click();
      });
    });
  };

  return (
    <Root className={classes.container}>
      <Box className={classes.heroContent}>
        <Box className={classes.topHeader}>
          <Box>
            <Box
              component="img"
              alt="Molina"
              src="/logo2.png"
              className={classes.logo1}
            />
          </Box>
          <Box>
            <Select
              className={classes.language}
              defaultValue=""
              displayEmpty
              onChange={(ev: any) =>
                downloadPDF(`${PDFFileNames[ev.target.value]}.pdf`)
              }>
              <MenuItem disabled value="">
                Select language
              </MenuItem>
              {languages.map((language) => (
                <MenuItem key={language} value={language}>
                  {t(`home.${language}`)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Box className={classes.heroText}>
          <Typography
            variant="h5"
            component="h5"
            className={classes.heroTitle1}>
            {t('home.title')}
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            mt={2}
            className={classes.heroTitle2}>
            {t('home.heroTitle')}
          </Typography>
          {/* <Typography
            variant="h4"
            component="h4"
            mt={2}
            className={classes.heroDescription}>
            {t('home.heroDescription')}
          </Typography> */}
        </Box>
        <Box className={classes.heroCTA}>
          <Button
            variant="contained"
            href={scheduleTransportationURL}
            className={classes.scheduleBtn}>
            {t('home.scheduleTransportation')}
          </Button>
          <Button variant="outlined" href={pcsURL} className={classes.pcsBtn}>
            {t('home.submitPCS')}
          </Button>
        </Box>
      </Box>
      <Box className={classes.mainContent}>
        <Box className={classes.left}>
          <Typography
            variant="h5"
            component="h5"
            className={classes.fontBitter}
            sx={(theme) => ({
              fontSize: 36,
              fontWeight: 700,
              [theme.breakpoints.down('md')]: {
                fontSize: 30,
                lineHeight: '40px',
              },
            })}>
            What is American Logistics?
          </Typography>
          <Typography
            mt={4}
            className={classes.fontSource}
            sx={(theme) => ({
              fontSize: 24,
              fontWeight: 'normal',
              lineHeight: '32px',
              [theme.breakpoints.down('md')]: {
                fontSize: 18,
                lineHeight: '24px',
              },
            })}>
            American Logistics is the transportation manager for all Molina,
            Medi-Cal Members. Our trip booking steps are quick and easy. We make
            sure each driver and vehicle meet the rules to keep you safe and
            track your trips.
          </Typography>
          <Typography
            mt={2}
            className={classes.fontSource}
            sx={(theme) => ({
              fontSize: 24,
              fontWeight: 'normal',
              lineHeight: '32px',
              [theme.breakpoints.down('md')]: {
                fontSize: 18,
                lineHeight: '24px',
              },
            })}>
            If you need access to transportation for any of your Medi Cal
            covered appointments, we can help you.
          </Typography>
          <Typography
            mt={5}
            className={classes.fontBitter}
            sx={(theme) => ({
              fontSize: 21,
              fontWeight: 700,
              [theme.breakpoints.down('md')]: {
                lineHeight: '32px',
              },
            })}>
            How to Schedule your Transportation:
          </Typography>
          <Typography
            mt={1}
            className={classes.fontSource}
            sx={{ fontSize: 15, fontWeight: 600 }}>
            BY PHONE:
          </Typography>
          <Typography
            className={classes.fontSource}
            sx={(theme) => ({
              fontSize: 21,
              fontWeight: 400,
              [theme.breakpoints.down('md')]: {
                fontSize: 16,
                lineHeight: '24px',
              },
            })}>
            Molina MEDI-CAL Reservation Line:
            <Box component="br" />
            <Box component="b">(844) 292-2688</Box> – American Logistics Call
            Center
          </Typography>
          <Typography
            mt={3}
            className={classes.fontSource}
            sx={{ fontSize: 15, fontWeight: 600 }}>
            BY WEB:
          </Typography>
          <Typography
            className={classes.fontSource}
            sx={(theme) => ({
              fontSize: 21,
              fontWeight: 400,
              [theme.breakpoints.down('md')]: {
                fontSize: 16,
                lineHeight: '24px',
              },
            })}>
            Schedule your transportation through our online portal:
          </Typography>
          <Button
            variant="outlined"
            color="secondary"
            href={scheduleTransportationURL}
            sx={{ marginTop: 3, marginBottom: 3 }}>
            Schedule Here
          </Button>

          {isLarge && <ContactInformation />}

          <Typography
            mt={4}
            className={classes.fontBitter}
            sx={(theme) => ({
              fontSize: 30,
              fontWeight: 700,
              lineHeight: '40px',
              [theme.breakpoints.down('md')]: {
                fontSize: 21,
                lineHeight: '32px',
              },
            })}>
            Schedule your Transportation at
            <Box component="br" />
            least 72 hours in advance
          </Typography>

          <Box className={classes.logo2} mt={12}>
            <Box component="img" alt="Molina Logo1" src="/logo.png" />
          </Box>
        </Box>
        <Box className={classes.right}>
          <Box
            sx={(theme) => ({
              padding: '0 30px',
              [theme.breakpoints.down('md')]: {
                padding: 0,
              },
            })}>
            <Typography
              mt={4}
              className={classes.fontBitter}
              sx={(theme) => ({
                fontSize: 21,
                fontWeight: 700,
                lineHeight: '32px',
                [theme.breakpoints.down('md')]: {
                  fontSize: 16,
                  lineHeight: '24px',
                },
              })}>
              Below are rides that do not require a transportation prescription
              (PCS Form) from your doctor.
            </Typography>

            <List sx={{ listStyleType: 'disc' }}>
              <ListItem className={classes.listItem}>
                Mass/Public Transit (Bus)
              </ListItem>

              <ListItem className={classes.listItem}>Uber/Lyft</ListItem>
              <ListItem className={classes.listItem}>
                Sedan/Van - No Driver Support Needed
              </ListItem>
              <ListItem className={classes.listItem}>
                Mileage Repayment
              </ListItem>
            </List>
          </Box>

          <Box
            sx={(theme) => ({
              padding: '0 30px',
              [theme.breakpoints.down('md')]: {
                padding: 0,
              },
            })}>
            <Typography
              mt={4}
              className={classes.fontBitter}
              sx={(theme) => ({
                fontSize: 21,
                fontWeight: 700,
                lineHeight: '32px',
                [theme.breakpoints.down('md')]: {
                  fontSize: 16,
                  lineHeight: '24px',
                },
              })}>
              Rides that do require a PCS Form from your doctor.
            </Typography>

            <List sx={{ listStyleType: 'disc' }}>
              <ListItem className={classes.listItem}>
                Car/Van with Door-to-Door/Driver Assistance Service
              </ListItem>
              <ListItem className={classes.listItem}>
                Wheelchair Accessible Van
                <Box component="br" />
                (Ramp or Lift)
              </ListItem>
              <ListItem className={classes.listItem}>
                Stretcher/Gurney/Litter Van
              </ListItem>
              <ListItem className={classes.listItem}>
                Non-Emergency Ambulance Services
              </ListItem>
            </List>
          </Box>

          <Box
            className={classes.whiteBorder}
            sx={{ padding: '30px', marginTop: 3 }}>
            <Typography
              className={classes.fontBitter}
              sx={(theme) => ({
                fontSize: 21,
                fontWeight: 700,
                lineHeight: '32px',
                [theme.breakpoints.down('md')]: {
                  fontSize: 18,
                  lineHeight: '26px',
                },
              })}>
              Your Medical Provider MUST fill out a PCS form - A prescription
              for transportation at:
            </Typography>

            {/* <List
              sx={{ listStyleType: 'disc', paddingLeft: 4, paddingRight: 4 }}>
              <ListItem className={classes.listItem}>
                Car/Van with Door-to-Door/Driver Assistance Service
              </ListItem>
              <ListItem className={classes.listItem}>
                Wheelchair Accessible Van
                <Box component="br" />
                (Ramp or Lift)
              </ListItem>
              <ListItem className={classes.listItem}>
                Stretcher/Gurney/Litter Van
              </ListItem>
              <ListItem className={classes.listItem}>
                Non-Emergency Ambulance Services
              </ListItem>
            </List> */}

            <Button
              variant="text"
              color="secondary"
              href={pcsURL}
              sx={{
                marginTop: 3,
                color: 'white',
                textDecoration: 'underline !important',
              }}>
              Molina.AmericanLogistics.com/PCS
            </Button>
          </Box>

          <Box className={classes.downloadBox}>
            <Box component="span">or</Box>
            <Button
              variant="text"
              sx={{ color: 'rgba(89, 239, 255, 1)' }}
              onClick={() => downloadPDF('Molina_PCS_Form.pdf')}>
              download the PCS Form
            </Button>
          </Box>

          <Typography
            className={classes.fontSource}
            mt={2}
            sx={(theme) => ({
              fontSize: 16,
              fontWeight: 400,
              lineHeight: '24px',
              paddingLeft: '30px',
              paddingRight: '30px',
              [theme.breakpoints.down('md')]: {
                padding: 0,
              },
            })}>
            Options: You can use the form provided in the link above or print
            the PDF and have it completed by your medical provider. Paper PCS
            forms will need to be submitted to your Molina Case Manager or to an
            American Logistics Trip Services agent for processing. Call the
            contact numbers provided for more information.
          </Typography>
        </Box>

        {!isLarge && <ContactInformation isLarge={isLarge} />}
      </Box>

      <Box className={classes.footer}>
        <Box component="img" alt="American Logistics" src="/PoweredBy.png" />
        <Box className={classes.footerMenu}>
          <Box
            component="div"
            sx={{ display: 'flex', alignItems: 'center', marginRight: 5 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 400,
              }}
              color="secondary">
              Questions? Call us at: (844) 292-2688
            </Typography>
          </Box>
          <Link
            href={__environment__.REACT_APP_PRIVACY_URL}
            target="_blank"
            className={classes.link}
            color="secondary">
            Privacy
          </Link>
          <Link
            href={__environment__.REACT_APP_PRIVACY_URL}
            target="_blank"
            className={classes.link}
            color="secondary">
            Terms
          </Link>
        </Box>
      </Box>
    </Root>
  );
}

function ContactInformation({ isLarge = true }) {
  return (
    <>
      <Divider
        sx={{ borderColor: '#fff', width: 300, marginTop: isLarge ? 0 : 3 }}
      />

      <Typography
        mt={3}
        className={classes.fontBitter}
        sx={{ fontSize: 21, fontWeight: 700 }}>
        Phone Numbers:
      </Typography>
      <Typography
        mt={1}
        className={classes.fontSource}
        sx={{ fontSize: 15, fontWeight: 600, lineHeight: '24px' }}>
        Molina:
        <Box component="br" />
        <Link
          href={`tel:${molinaTelephone}`}
          underline="none"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: '24px',
            color: 'white',
          }}>
          {molinaTelephone}
        </Link>
      </Typography>
      <Typography
        mt={1}
        mb={3}
        className={classes.fontSource}
        sx={{ fontSize: 15, fontWeight: 600, lineHeight: '24px' }}>
        American Logistics:
        <Box component="br" />
        <Link
          href={`tel:${americanlogisticsTelephone}`}
          underline="none"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: '24px',
            color: 'white',
          }}>
          {americanlogisticsTelephone}
        </Link>
        <Box component="br" />
        <Link
          href={americanlogisticsURL}
          underline="always"
          sx={{
            fontSize: 15,
            fontWeight: 600,
            lineHeight: '24px',
            color: 'white',
            textDecorationColor: 'white',
          }}>
          Molina.AmericanLogistics.com
        </Link>
      </Typography>

      {isLarge && <Divider sx={{ borderColor: '#fff', width: 300 }} />}
    </>
  );
}
